/**
 * Sentry SDK configuration and noise filtering for the dog quiz.
 *
 * Loaded after the Sentry SDK bundle on the dog quiz page.
 * Filters third-party errors, console.log noise, browser extensions,
 * and other non-actionable events before they reach Sentry.
 */

const sentryErrorPatternsToIgnore = [
  /fbq is not defined/i,
  /ReferenceError: Can't find variable: fbq/i,
  /RB is not defined/i,
  /Can't find variable: RB/i,
  /Could not establish connection\. Receiving end does not exist/i,
  /Error invoking postMessage: Java object is gone/i,
  /Error invoking enableDidUserTypeOnKeyboardLogging/i,
  /Error invoking enableButtonsClickedMetaDataLogging/i,
  /WKWebView API client did not respond to this postMessage/i,
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i,
  /AbortError: The operation was aborted/i,
  /SecurityError: Failed to read the 'localStorage' property from 'Window'/i,
  /SecurityError: Failed to read the 'cssRules' property from 'CSSStyleSheet'/i,
  /Not allowed to access cross-origin stylesheet/i,
  /Failed to execute 'transaction' on 'IDBDatabase'/i,
  /feature named `hover` was not found/i,
  /feature named `pageObserver` was not found/i,
  // Facebook navigation metrics
  /^FBNav/,
  // HelpScout widget
  /hxp-chat-suppression/i,
  /hxp-modal-detection/i,
  // Password manager extensions
  /\[DetectLoginForms\.js\]/,
  // Local dev hot reload
  /\[HotReload\]/,
  // Translation extensions
  /\[PageTranslationRequiredListener\.js\]/,
  // Viewport debugging
  /^innerHeight:/,
  // PageFly extension
  /Run PageFly/i,
  /Script loaded successfully/i,
  // Popup app debug
  /Ecom popup data element not found/i,
  // Obfuscated third-party scripts (trekkie, popup app embeds) — the
  // string-array indexing shape (e.g. `c[r(906)]`) never appears in our
  // own gulp/uglify output.
  /[\w$]+\[[\w$]+\(\d+\)\] is not a function/,
  /_0x[0-9a-f]+ is not an Object/i,
  // Chrome reader mode
  /DomDistiller/i,
  // Amplitude SDK
  /Amplitude Logger/i,
  // Klaviyo SDK debug
  /Klaviyo button not found/i,
  // Shopify consent API deprecation
  /userDataCanBeSold is deprecated/i,
  // Third-party metrics
  /failed to send metrics/i,
  // Quiz debug logging (not errors)
  /No allergies found for the dog/i,
  /No active medium box variants found/i,
  /^(Beef|Chicken|Beef,Chicken)$/,
  // Cart debug logging
  /^Adding Items:/,
  /^Update Data for Removal:/,
  // Generic debug logs
  /^changes$/,
  /^click$/,
  // iOS form detection
  /iOS: formmutation/i,
  /Dispatch formmutation/i,
  // Fingerprint debug hashes
  /^text_and_dom:|^text_only:|^dom_only:/,
  // Facebook pixel warnings
  /\[Meta Pixel\]/,
  // Shopify cart API operational errors (409 conflicts, 429 rate limits)
  /Request failed with status (409|429) from.*cart\/(update|add|change)\.js/i,
  /409 Conflict/i,
  /429 Too Many Requests/i,
];

const sentryFramePatternsToIgnore = [
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
  /web-pixel-shopify-app-pixel/i,
  /cdn\/wpm\//i,
  /flatpickr\/dist\/esm\/index/i,
  /onsite-triggering-v2/i,
  /pagefly/i,
  /amplitude/i,
];

const getSentryEventMessage = (event, hint) => {
  const hintMessage = hint?.originalException?.message || hint?.originalException?.toString?.();
  const eventMessage = event?.message
    || event?.exception?.values?.[0]?.value
    || event?.logentry?.formatted
    || event?.logentry?.message;

  return String(eventMessage || hintMessage || '');
};

const eventHasIgnoredFrame = (event) => {
  const frames = event?.exception?.values?.flatMap((exception) => exception?.stacktrace?.frames || []) || [];
  return frames.some((frame) => {
    const filename = String(frame?.filename || '');
    const functionName = String(frame?.function || '');
    return sentryFramePatternsToIgnore.some((pattern) => pattern.test(filename) || pattern.test(functionName));
  });
};

const eventOnlyHasIgnoredFrames = (event) => {
  const frames = event?.exception?.values?.flatMap((exception) => exception?.stacktrace?.frames || []) || [];
  if (frames.length === 0) return false;
  return frames.every((frame) => {
    const filename = String(frame?.filename || '');
    const functionName = String(frame?.function || '');
    return sentryFramePatternsToIgnore.some((pattern) => pattern.test(filename) || pattern.test(functionName));
  });
};

const eventHasStackTrace = (event) => {
  const frames = event?.exception?.values?.flatMap((exception) => exception?.stacktrace?.frames || []) || [];
  return frames.length > 0;
};

const shouldDropSentryEvent = (event, hint) => {
  const message = getSentryEventMessage(event, hint);
  const url = event?.request?.url || '';

  if (/127\.0\.0\.1:9292/.test(url)) {
    return true;
  }

  if (sentryErrorPatternsToIgnore.some((pattern) => pattern.test(message))) {
    return true;
  }

  if (/NetworkError: Load failed/i.test(message) && eventHasIgnoredFrame(event)) {
    return true;
  }

  if (/SecurityError: Not allowed to access cross-origin stylesheet/i.test(message) && eventHasIgnoredFrame(event)) {
    return true;
  }

  if (/invalid origin/i.test(message) && !eventHasStackTrace(event)) {
    return true;
  }

  if (/null is not an object \(evaluating ['"].+['"]\)/i.test(message) && !eventHasStackTrace(event)) {
    return true;
  }

  // Drop errors where ALL stack frames originate from ignored third-party sources
  if (eventHasStackTrace(event) && eventOnlyHasIgnoredFrames(event)) {
    return true;
  }

  return false;
};

Sentry.init({
  dsn: "https://d9c04835f6e09d2f5e182b508ff9fd61@o4505962399858688.ingest.us.sentry.io/4507612751200256",
  ignoreErrors: sentryErrorPatternsToIgnore,
  beforeSend(event, hint) {
    if (shouldDropSentryEvent(event, hint)) {
      return null;
    }

    event.tags = {
      ...event.tags,
      feature: event.tags?.feature || 'dog_quiz',
      route: event.tags?.route || window.location.pathname,
      page_type: event.tags?.page_type || 'dog_quiz',
    };

    const eventMessage = getSentryEventMessage(event, hint);
    if (/Invalid response format/i.test(eventMessage) && !event.fingerprint) {
      event.fingerprint = ['dog-quiz-recommendation-invalid-response'];
    }

    return event;
  },
});
